import type { ProblemDetails } from "./api-client/apiModels.gen";
import { isResponseError } from "./responseError";

/**
 * Domain error codes the frontend branches on, surfaced in the ProblemDetails "code" member
 * (see ApiController.ErrorCodeKey on the backend). Values mirror the backend {Feature}Errors codes.
 */
export const ApiErrorCode = {
  UploadTokenNotFound: "DocumentUploadToken.NotFound",
  FileTooLarge: "FileUpload.FileTooLarge",
  InvalidFileType: "FileUpload.InvalidFileType",
  EmailAttachmentConversionUnsupported: "Document.EmailAttachmentConversionUnsupported",
  UploadConversionFailed: "Document.UploadConversionFailed",
  StorageLimitReached: "Document.StorageLimitReached",
  MaxDocumentsPerClientReached: "Document.MaxDocumentsPerClientReached",
  VolunteerUsernameAlreadyTaken: "Volunteer.UsernameAlreadyTaken",
  AmsBookingAlreadyBooked: "AmsBooking.AlreadyBooked",
  AmsBookingPending: "AmsBooking.BookingPending",
  AmsBookingFirstNameMissing: "AmsBooking.FirstNameMissing",
  AmsBookingLastNameMissing: "AmsBooking.LastNameMissing",
  AmsBookingInsuranceNumberMissing: "AmsBooking.InsuranceNumberMissing",
  AmsBookingInsuranceNumberNotUsable: "AmsBooking.InsuranceNumberNotUsable",
  AmsBookingZipCodeMissing: "AmsBooking.ZipCodeMissing",
  AmsBookingSidNotConfigured: "AmsBooking.SidNotConfigured",
  AmsBookingNoVidForZipCode: "AmsBooking.NoVidForZipCode",
  AmsBookingRequestFailed: "AmsBooking.RequestFailed",
  AmsBookingWritesDisabled: "AmsBooking.WritesDisabled",
  VolunteerUsernameLockedByPortalAccount: "Volunteer.UsernameLockedByPortalAccount",
  CounselorUserCodeTaken: "Counselor.UserCodeTaken",
  DutySchedulePeriodOverlaps: "DutySchedule.PeriodOverlaps",
  DutyScheduleNotEditable: "DutySchedule.NotEditable",
  DutyScheduleSlotNotFound: "DutySchedule.SlotNotFound",
  DutyScheduleInactiveCounselorsAssigned: "DutySchedule.InactiveCounselorsAssigned",
  DutyScheduleAlreadyReleased: "DutySchedule.AlreadyReleased",
  DutyScheduleNotFound: "DutySchedule.NotFound",
  EdictNotDebtSettlementRegister: "Edict.NotDebtSettlementRegister",
  EdictSourceUnavailable: "Edict.SourceUnavailable",
  EdictDocumentNotFound: "Edict.DocumentNotFound",
  PasswordRejected: "User.PasswordRejected",
  IdentityProviderUnavailable: "User.IdentityProviderUnavailable",
  IdentityProviderError: "User.IdentityProviderError",
  ClaimBalanceListChanged: "Claim.BalanceListChanged",
  AssetInventoryExpensePartnerNotFound: "AssetInventory.ExpensePartnerNotFound",
  AssetInventoryExpensePartnerIsSelf: "AssetInventory.ExpensePartnerIsSelf",
  CourtFileNumberPrefixAlreadyInUse: "Court.FileNumberPrefixAlreadyInUse",
  CourtDistrictAlreadyAssigned: "Court.DistrictAlreadyAssigned",
} as const;

/** The code member of a parsed error body, or undefined when the body carries none. */
function codeOf(body: unknown): string | undefined {
  return (body as ProblemDetails | null)?.code;
}

/** Reads the stable domain error code from a ProblemDetails error response, if present. */
export async function getApiErrorCode(error: unknown): Promise<string | undefined> {
  if (!isResponseError(error)) {
    return undefined;
  }

  try {
    return codeOf(await error.response.clone().json());
  } catch {
    // The body is empty or not JSON — no code to go on.
    return undefined;
  }
}

/**
 * Same, for callers holding the raw response body as text rather than a Response — the XHR upload
 * path, which reports progress and so cannot go through fetch.
 */
export function getApiErrorCodeFromBody(body: string): string | undefined {
  try {
    return codeOf(JSON.parse(body));
  } catch {
    return undefined;
  }
}
